import React from 'react';
import { Link } from 'react-router-dom';
import { AccentButton } from '../components/Button';
import ThemeSwitch from '../components/ThemeSwitch';

const Section = ({ title, children }) => (
  <div className="flex flex-col gap-3">
    <h2 className="text-base font-semibold text-[#121212] dark:text-[#D0D0D0]">{title}</h2>
    <div className="text-sm text-[#7B7B7B] dark:text-[#848484] leading-relaxed flex flex-col gap-2">
      {children}
    </div>
  </div>
);

const PrivacyPage = () => (
  <div className="min-h-screen bg-white dark:bg-[#121212] flex flex-col">

    {/* Header */}
    <header className="h-[64px] border-b border-[#ECECEC] dark:border-[#202020] flex items-center px-6 justify-between shrink-0">
      <Link to="/" className="flex items-center">
        <span className="text-xl font-bold text-[#121212] dark:text-[#D0D0D0]">Clipboarded</span>
        <span className="text-xl font-normal italic text-[#121212] dark:text-[#D0D0D0] ml-1">.me</span>
      </Link>
      <div className="flex items-center gap-3">
        <ThemeSwitch />
        <Link to="/sign-in">
          <AccentButton variant="secondary" className="h-[36px]">Sign in</AccentButton>
        </Link>
        <Link to="/sign-up">
          <AccentButton variant="primary" className="h-[36px]">Get started</AccentButton>
        </Link>
      </div>
    </header>

    {/* Content */}
    <main className="flex-1 flex flex-col items-center">
      <div className="w-full max-w-2xl px-6 py-16 flex flex-col gap-10">

        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-[#121212] dark:text-[#D0D0D0] tracking-tight">Privacy Policy</h1>
          <p className="text-sm text-[#7B7B7B] dark:text-[#848484]">Last updated: March 22, 2026</p>
        </div>

        <Section title="1. Overview">
          <p>Clipboarded.me ("we", "our", or "us") is a cross-device clipboard management service. This Privacy Policy explains how we collect, use, and protect your data when you use our service at clipboarded.me.</p>
          <p>We take privacy seriously. Your clipboard data is yours — we store it only to sync it across your devices and share it with people you explicitly choose.</p>
        </Section>

        <Section title="2. Data We Collect">
          <p><strong className="text-[#121212] dark:text-[#D0D0D0]">Account data:</strong> Email address and hashed password (or magic code for passwordless login). We do not store plain-text passwords.</p>
          <p><strong className="text-[#121212] dark:text-[#D0D0D0]">Clipboard content:</strong> Text, URLs, images, and files you paste into Clipboarded folders. This data is stored on our servers to enable sync across your devices.</p>
          <p><strong className="text-[#121212] dark:text-[#D0D0D0]">Usage data:</strong> Basic technical data such as IP address, browser type, and activity timestamps — used for security, abuse prevention, and improving the service.</p>
          <p><strong className="text-[#121212] dark:text-[#D0D0D0]">Device information:</strong> Device type (OS) recorded when clipboard items are created, shown in the item card UI.</p>
        </Section>

        <Section title="3. How We Use Your Data">
          <p>We use your data exclusively to:</p>
          <ul className="flex flex-col gap-1 list-disc list-inside">
            <li>Sync your clipboard content across your devices</li>
            <li>Enable folder sharing with people you choose</li>
            <li>Authenticate you securely</li>
            <li>Send transactional emails (login codes, account changes)</li>
            <li>Improve the service and fix bugs</li>
          </ul>
          <p>We do <strong className="text-[#121212] dark:text-[#D0D0D0]">not</strong> sell your data. We do not use your clipboard content for advertising or training AI models.</p>
        </Section>

        <Section title="4. Data Storage & Security">
          <p>Clipboard content and files are stored on secure cloud infrastructure. Data is encrypted at rest and in transit (TLS).</p>
          <p>Authentication sessions are managed with signed tokens and appropriate expiration policies.</p>
          <p>We apply rate limiting and abuse prevention measures to protect your account.</p>
        </Section>

        <Section title="5. Data Sharing">
          <p>Your data is private by default. We share it only in these circumstances:</p>
          <ul className="flex flex-col gap-1 list-disc list-inside">
            <li><strong className="text-[#121212] dark:text-[#D0D0D0]">With people you invite</strong> — when you share a folder link, recipients can view and paste from that folder</li>
            <li><strong className="text-[#121212] dark:text-[#D0D0D0]">With infrastructure providers</strong> — cloud storage and hosting providers necessary to operate the service</li>
            <li><strong className="text-[#121212] dark:text-[#D0D0D0]">When required by law</strong> — if compelled by a valid legal process</li>
          </ul>
          <p>We never share your data with third-party advertisers or data brokers.</p>
        </Section>

        <Section title="6. Data Retention">
          <p>Your clipboard data is retained as long as your account is active. Items moved to the Trash Bin are permanently deleted after 30 days.</p>
          <p>You can delete individual items, folders, or your entire account at any time. Account deletion permanently removes all your data from our systems within 30 days.</p>
        </Section>

        <Section title="7. Your Rights">
          <p>You have the right to:</p>
          <ul className="flex flex-col gap-1 list-disc list-inside">
            <li>Access all data we hold about you</li>
            <li>Export your clipboard data</li>
            <li>Correct inaccurate account information</li>
            <li>Delete your account and all associated data</li>
            <li>Opt out of non-essential communications</li>
          </ul>
          <p>To exercise any of these rights, contact us at <a href="mailto:privacy@clipboarded.me" className="text-[#121212] dark:text-[#D0D0D0] underline">privacy@clipboarded.me</a>.</p>
        </Section>

        <Section title="8. Cookies">
          <p>We use minimal cookies — only what is necessary for authentication (session token). We do not use tracking cookies or third-party analytics cookies.</p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>Clipboarded.me is not intended for users under 13 years of age. We do not knowingly collect data from children.</p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>We may update this policy from time to time. We will notify registered users of significant changes via email. Continued use of the service after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="11. Contact">
          <p>For privacy-related questions or requests:</p>
          <p><a href="mailto:privacy@clipboarded.me" className="text-[#121212] dark:text-[#D0D0D0] underline">privacy@clipboarded.me</a></p>
        </Section>

      </div>
    </main>

    {/* Footer */}
    <footer className="border-t border-[#ECECEC] dark:border-[#202020] px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center">
        <span className="text-sm font-bold text-[#121212] dark:text-[#D0D0D0]">Clipboarded</span>
        <span className="text-sm font-normal italic text-[#121212] dark:text-[#D0D0D0] ml-1">.me</span>
      </div>
      <p className="text-xs text-[#7B7B7B] dark:text-[#848484]">&copy; {new Date().getFullYear()} Clipboarded.me</p>
      <div className="flex gap-4">
        <Link to="/" className="text-xs text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] transition-colors">Home</Link>
        <Link to="/sign-in" className="text-xs text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] transition-colors">Sign in</Link>
        <Link to="/sign-up" className="text-xs text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] transition-colors">Sign up</Link>
        <Link to="/privacy" className="text-xs text-[#121212] dark:text-[#D0D0D0] font-medium">Privacy</Link>
      </div>
    </footer>

  </div>
);

export default PrivacyPage;
